import React, { useState } from 'react';
import { alpha, Box } from '@mui/material';
import { styled } from '@mui/system';
import throttle from 'lodash/throttle';

interface CursorCircleProps {
    visible     : boolean;
    colorIndex  : number;
    angle       : number;
}

const CursorCircleWrapper = styled(Box, {
    shouldForwardProp: prop => prop !== 'visible' && prop !== 'colorIndex' && prop !== 'angle'
})<CursorCircleProps>(({ theme, colorIndex = 0, angle = 0, visible }) => {
    
    const colorArray = [
        theme.palette.secondary,
        theme.palette.primary,
        theme.palette.info,
        theme.palette.success,
        theme.palette.warning,
        theme.palette.error
    ];
    const color = colorArray[colorIndex % colorArray.length] || colorArray[0];
    
    return {
        position        : 'fixed',
        width           : 150,
        height          : 150,
        backgroundColor : alpha( color.light, 0.3),
        borderRadius    : '50%',
        pointerEvents   : 'none',
        transform       : `translate(-50%, -50%) rotate(${angle}deg)`,
        zIndex          : 1000,
        opacity         : visible ? 1 : 0,
        transition      : 'opacity 0.5s ease-in-out', // Adjust the duration for the fade effect
        border          : `1px solid ${color.dark}`,
        /*
        '&::after': {
            content: '""',
            position: 'absolute',
            width: 10,
            height: 10,
            backgroundColor: 'black',
            borderRadius: '50%',
            pointerEvents: 'none',
            zIndex: -1,
        },
        */
        
    }
});

interface Coords {
    x : number,
    y : number
}

export const CursorCircle: React.FC = () => {
    
    const [position,    setPosition]            = React.useState({ x: 0, y: 0 });
    const [visible,     setVisible]             = React.useState( false );
    const [colorIndex,  setColorIndex]          = React.useState(0)
    const [angle,       setAngle]               = React.useState(0);
    const ref                                   = React.useRef<HTMLDivElement>(null);
    const refCenterMem                          = React.useRef<Coords>({x : 0, y : 0});
    const [refCenter,   setRefCenter]           = React.useState({ x: 0, y: 0 });
    
    let timer : NodeJS.Timeout;

    // Toggle Dark mode when !visible => visible
    React.useEffect(() => {
        if(visible) 
            setColorIndex(prev => prev + 1)
    },[visible])

    React.useEffect(() => {
        if (typeof window === 'undefined' || !window.document) {
            return;
        }
        const handleMouseMove = (event: MouseEvent) => {

            setPosition({ 
                x: event.clientX, 
                y: event.clientY
            });

            const threshold = 10, thresholdTop = 100;

            // Move out of screen
            if (event.clientY <= thresholdTop || event.clientX <= threshold || event.clientX >= window.innerWidth - threshold || event.clientY >= window.innerHeight - threshold) {
                
                setVisible(false);

            // Move inside screen
            } else {

                setVisible(true); // Set Visible
                clearTimeout(timer);
                timer = setTimeout(() => {
                    setVisible(false); // Set Invisible
                }, 500); // Adjust the delay before fading out
                
            }
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(timer);
        };
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined' || !window.document) {
            return;
        }
        const handleMouseMove = () => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                const x = rect.left + rect.width  / 2;
                const y = rect.top  + rect.height / 2;
                setRefCenter({ x, y });
            }
        }
        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            clearTimeout(timer);
        };
    }, []);


    React.useEffect(() => {
        const {x,y} = refCenter;
        const   dx = x - refCenterMem.current.x,
                dy = y - refCenterMem.current.y;
        const euclid    = Math.pow(dx*dx + dy*dy, 0.5);
        const newAngle  = Math.atan2(dy, dx) * 180 / Math.PI
        if(euclid > 0){
            setAngle( newAngle )
            refCenterMem.current = {x,y} as Coords;
        }
    },[refCenter?.y, refCenter?.y]);

    return (
        
            <CursorCircleWrapper 
                ref         = {ref}
                visible     = {visible} 
                angle       = {angle - 45} 
                colorIndex  = {colorIndex}
                style       = {{
                    top         : position.y, 
                    left        : position.x,
                    // border      : '1px solid black',
                }} 
            >
                {
                    false && 
                    <Box sx={{position:'absolute', top:0,left:0,right:0,bottom:0,border:'1px solid black'}} />
                }
                {
                    false && 
                    <Box sx={{position:'absolute', top:'50%',left:'50%',transform:'translatexy(-50%,-50%)',fontSize:10}}>
                        {ref.current && `T:${angle.toFixed(2)}, x:${refCenter.x.toFixed(2)}, y:${refCenter.y.toFixed(2)}`}
                    </Box>
                }
            </CursorCircleWrapper>
        
    );
};

export default CursorCircle